import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../config/firebase';

class StateDepartmentsService {
  constructor() {
    this.collectionName = 'stateDepartments';
  }

  // Get departments data for a specific financial year
  async getDepartmentsData(financialYear) {
    try {
      const docRef = doc(db, this.collectionName, financialYear);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          success: true,
          data: {
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate()
          }
        };
      } else {
        return {
          success: false,
          message: 'No data found for this financial year'
        };
      }
    } catch (error) {
      console.error('Error getting departments data:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  // Save departments data with real-time auto-save (no status/submission logic)
  async saveDepartmentsData(departmentsData, userId) {
    try {
      // Validate input data
      if (!departmentsData || !departmentsData.financialYear) {
        throw new Error('Invalid departments data: financialYear is required');
      }

      if (!userId) {
        throw new Error('User ID is required for saving departments data');
      }

      const docRef = doc(db, this.collectionName, departmentsData.financialYear);
      const docSnap = await getDoc(docRef);
      
      const timestamp = serverTimestamp();
      
      // Prepare the data to save
      const dataToSave = {
        financialYear: departmentsData.financialYear,
        departments: departmentsData.departments || [],
        totalAmount: departmentsData.totalAmount || 0,
        updatedAt: timestamp,
        updatedBy: userId
      };
      
      if (docSnap.exists()) {
        // Update existing document - preserve creation metadata
        const existingData = docSnap.data();
        await updateDoc(docRef, {
          ...dataToSave,
          createdAt: existingData.createdAt || timestamp,
          createdBy: existingData.createdBy || userId
        });
      } else {
        // Create new document
        await setDoc(docRef, {
          ...dataToSave,
          createdAt: timestamp,
          createdBy: userId
        });
      }
      
      console.log('Departments data saved successfully for FY:', departmentsData.financialYear);
      return {
        success: true,
        message: 'Departments data saved successfully'
      };
    } catch (error) {
      console.error('Error saving departments data:', error);
      return {
        success: false,
        message: error.message || 'Failed to save departments data'
      };
    }
  }

  // Get all departments data (for reports/analytics)
  async getAllDepartmentsData() {
    try {
      const querySnapshot = await getDocs(collection(db, this.collectionName));
      const data = [];
      
      querySnapshot.forEach((doc) => {
        const docData = doc.data();
        data.push({
          id: doc.id,
          ...docData,
          createdAt: docData.createdAt?.toDate(),
          updatedAt: docData.updatedAt?.toDate()
        });
      });
      
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error getting all departments data:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  // Get departments data by financial year range
  async getDepartmentsDataByYearRange(startYear, endYear) {
    try {
      const data = [];
      
      // Generate financial year codes in the range
      for (let year = parseInt(startYear); year <= parseInt(endYear); year++) {
        const fyCode = `${year}-${String(year + 1).slice(-2)}`;
        const result = await this.getDepartmentsData(fyCode);
        
        if (result.success && result.data) {
          data.push({
            financialYear: fyCode,
            ...result.data
          });
        }
      }
      
      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error getting departments data by year range:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  // Get departments fund utilization report
  async getDepartmentsFundReport(financialYear) {
    try {
      const docRef = doc(db, this.collectionName, financialYear);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return {
          success: false,
          message: 'No department data found for this financial year'
        };
      }
      
      const data = docSnap.data();
      const departments = data.departments || [];
      
      const report = {
        financialYear,
        departments: [],
        summary: {
          totalAllocated: 0,
          totalDepartments: departments.length,
          averageAllocation: 0
        }
      };
      
      departments.forEach(dept => {
        const amount = parseFloat(dept.amount) || 0;
        
        report.departments.push({
          name: dept.name,
          amount: amount,
          percentage: 0 // Will be calculated after total is known
        });
        
        report.summary.totalAllocated += amount;
      });
      
      // Calculate percentages
      if (report.summary.totalAllocated > 0) {
        report.departments.forEach(dept => {
          dept.percentage = ((dept.amount / report.summary.totalAllocated) * 100).toFixed(2);
        });
        
        report.summary.averageAllocation = 
          (report.summary.totalAllocated / report.summary.totalDepartments).toFixed(2);
      }
      
      // Sort departments by amount (highest first)
      report.departments.sort((a, b) => b.amount - a.amount);
      
      return {
        success: true,
        data: report
      };
    } catch (error) {
      console.error('Error generating departments fund report:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  // Bulk update departments data (for administrative operations)
  async bulkUpdateDepartmentsData(updates, userId) {
    try {
      const results = {
        successful: [],
        failed: []
      };
      
      for (const update of updates) {
        try {
          const result = await this.saveDepartmentsData(update.data, userId);
          if (result.success) {
            results.successful.push(update.financialYear);
          } else {
            results.failed.push({ 
              financialYear: update.financialYear, 
              error: result.message 
            });
          }
        } catch (error) {
          results.failed.push({ 
            financialYear: update.financialYear, 
            error: error.message 
          });
        }
      }
      
      return {
        success: true,
        data: results
      };
    } catch (error) {
      console.error('Error in bulk update:', error);
      throw new Error('Failed to perform bulk update');
    }
  }

  // Delete departments data for a financial year (administrative function)
  async deleteDepartmentsData(financialYear) {
    try {
      const docRef = doc(db, this.collectionName, financialYear);
      await deleteDoc(docRef);
      
      return {
        success: true,
        message: 'Departments data deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting departments data:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }

  // Get departments summary across all financial years
  async getDepartmentsSummary() {
    try {
      const allData = await this.getAllDepartmentsData();
      
      if (!allData.success) {
        return allData;
      }
      
      const summary = {
        totalFinancialYears: allData.data.length,
        totalDepartments: 0,
        totalAllocatedAmount: 0,
        yearlyBreakdown: [],
        departmentFrequency: {}
      };
      
      allData.data.forEach(yearData => {
        const departments = yearData.departments || [];
        const yearTotal = departments.reduce((sum, dept) => sum + (parseFloat(dept.amount) || 0), 0);
        
        summary.totalDepartments += departments.length;
        summary.totalAllocatedAmount += yearTotal;
        
        summary.yearlyBreakdown.push({
          financialYear: yearData.financialYear,
          departmentCount: departments.length,
          totalAmount: yearTotal
        });
        
        // Track department frequency
        departments.forEach(dept => {
          const deptName = dept.name;
          summary.departmentFrequency[deptName] = 
            (summary.departmentFrequency[deptName] || 0) + 1;
        });
      });
      
      return {
        success: true,
        data: summary
      };
    } catch (error) {
      console.error('Error generating departments summary:', error);
      return {
        success: false,
        message: error.message
      };
    }
  }
}

export const stateDepartmentsService = new StateDepartmentsService();